import React, { useState, useEffect, useCallback } from 'react';
import { useBrandData } from './useBrandData';
import { Tables } from '../../../database.types';
import { BrainIcon, Info } from "lucide-react"
import { useBrands } from '@/lib/BrandsProvider'
import NoBrandSelected from '../NoBrandSelected'
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
// Removed withSupabaseReconnect to prevent state loss on tab switch
import { Toaster } from "@/components/ui/toaster"
import { BrandGeniusForm } from './BrandGeniusForm'
import { BrandGeniusWizard } from './BrandGeniusWizard'
import { Button } from "@/components/ui/button"
import { LayoutGrid, Workflow, Download, Upload } from "lucide-react"
import fieldMetadata from '../../lib/fieldMetadata.json'
import { useToast } from "@/components/ui/use-toast"
import { useRole } from '@/lib/useRole'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
type Brand = Tables<'brands'>;

export const BrandGenius: React.FC = () => {
    const { selectedBrand } = useBrands();
    const { toast } = useToast();
    const { isAdmin } = useRole();
    const {
        fetchBrandDetails,
        updateBrandField,
    } = useBrandData(selectedBrand?.id);

    const [localCurrentBrand, setLocalCurrentBrand] = useState<Brand | null>(null);
    const [mode, setMode] = useState<'form' | 'wizard'>('form');
    const [hasInitializedMode, setHasInitializedMode] = useState(false);

    // For tracking unsaved changes in form mode
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Calculate brand completion percentage with useCallback to prevent infinite loops
    const calculateBrandCompletion = useCallback((brand: Brand | null) => {
        if (!brand) return 0;
        const visibleFields = Object.keys(fieldMetadata).filter(key => {
            const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
            return !metadata?.hidden && key !== 'status';
        });
        const totalFields = visibleFields.length;
        const filledFields = visibleFields.filter(key => {
            const value = brand[key as keyof Brand];
            return value !== null && value !== undefined && value !== '';
        }).length;
        return (filledFields / totalFields) * 100;
    }, []);

    // Export brand data as JSON
    const handleExportBrand = useCallback(() => {
        if (!localCurrentBrand) {
            toast({
                title: "Export Failed",
                description: "No brand data to export",
                variant: "destructive",
            });
            return;
        }

        // Filter out system fields and include only relevant brand data
        const exportData = Object.keys(fieldMetadata).reduce((acc, key) => {
            const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
            // Include all non-hidden fields and key fields
            if (!metadata?.hidden || metadata?.keyField) {
                const value = localCurrentBrand[key as keyof Brand];
                if (value !== null && value !== undefined && value !== '') {
                    acc[key] = value;
                }
            }
            return acc;
        }, {} as Record<string, any>);

        // Add metadata
        const exportPayload = {
            version: "1.0",
            exportDate: new Date().toISOString(),
            brandName: localCurrentBrand.brand_name,
            data: exportData
        };

        // Create and download the JSON file
        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${localCurrentBrand.brand_name || 'brand'}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
            title: "Export Successful",
            description: `Brand data exported as ${a.download}`,
        });
    }, [localCurrentBrand, toast]);

    // Import brand data from JSON
    const handleImportBrand = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            try {
                const text = await file.text();
                const importData = JSON.parse(text);

                // Validate the import format
                if (!importData.version || !importData.data) {
                    throw new Error("Invalid import file format");
                }

                // Show confirmation dialog
                const confirmImport = window.confirm(
                    `Are you sure you want to import brand data? This will override the following fields:\n\n` +
                    `${Object.keys(importData.data).join(', ')}\n\n` +
                    `Current brand: ${localCurrentBrand?.brand_name}\n` +
                    `Importing from: ${importData.brandName || 'Unknown'}`
                );

                if (!confirmImport) return;

                // Apply the imported data
                let updatedCount = 0;
                const errors: string[] = [];

                for (const [key, value] of Object.entries(importData.data)) {
                    // Skip if field doesn't exist in metadata
                    if (!(key in fieldMetadata)) {
                        errors.push(`Unknown field: ${key}`);
                        continue;
                    }

                    // Skip system fields that shouldn't be imported
                    if (key === 'id' || key === 'created_at' || key === 'updated_at') {
                        continue;
                    }

                    try {
                        await updateBrandField(selectedBrand!.id, key, value);
                        updatedCount++;
                    } catch (error) {
                        errors.push(`Failed to update ${key}: ${error}`);
                    }
                }

                // Refresh the brand data
                const updatedBrand = await fetchBrandDetails(selectedBrand!.id);
                if (updatedBrand) {
                    setLocalCurrentBrand(updatedBrand);
                }

                // Show result
                if (errors.length > 0) {
                    toast({
                        title: "Import Completed with Warnings",
                        description: `Updated ${updatedCount} fields. Errors: ${errors.join(', ')}`,
                        variant: "default",
                    });
                } else {
                    toast({
                        title: "Import Successful",
                        description: `Successfully updated ${updatedCount} brand fields`,
                    });
                }

            } catch (error) {
                console.error('Import error:', error);
                toast({
                    title: "Import Failed",
                    description: error instanceof Error ? error.message : "Failed to import brand data",
                    variant: "destructive",
                });
            }
        };

        input.click();
    }, [localCurrentBrand, selectedBrand, updateBrandField, fetchBrandDetails, toast]);

    useUnsavedChangesPrompt(hasUnsavedChanges);

    // Reset state when brand changes
    useEffect(() => {
        setLocalCurrentBrand(null);
        setHasUnsavedChanges(false);
        setHasInitializedMode(false);
    }, [selectedBrand?.id]);

    useEffect(() => {
        const loadBrandData = async () => {
            if (!selectedBrand?.id) {
                return;
            }

            try {
                console.log('Initial load of brand data for ID:', selectedBrand.id);
                const brandData = await fetchBrandDetails(selectedBrand.id);
                if (brandData) {
                    setLocalCurrentBrand(brandData);
                    
                    // Only set default mode based on completion during initial load
                    if (!hasInitializedMode) {
                        const completion = calculateBrandCompletion(brandData);
                        setMode(completion < 100 ? 'wizard' : 'form');
                        setHasInitializedMode(true);
                    }
                }

            } catch (error) {
                console.error('Error loading brand data:', error);
            }
        };

        loadBrandData();
    }, [selectedBrand?.id, fetchBrandDetails, hasInitializedMode]);



    if (!selectedBrand) {
        return <NoBrandSelected />;
    }

    if (!localCurrentBrand) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
                    <div className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/40 rounded-2xl border border-purple-200/50 shadow-sm p-4 md:p-8">
                        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                        <div className="relative">
                            <div className="flex items-center gap-2 md:gap-4">
                                <div className="p-2 md:p-3 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg">
                                    <BrainIcon className="h-5 w-5 md:h-8 md:w-8 text-purple-600" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 md:mb-2">
                                        <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 bg-clip-text text-transparent">Brand Genius</h1>
                                        {/* Tooltip icon - visible on all screen sizes */}
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button 
                                                        type="button"
                                                        className="p-1.5 rounded-full hover:bg-purple-100/50 transition-colors flex-shrink-0"
                                                        aria-label="Brand Genius information"
                                                    >
                                                        <Info className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="max-w-xs p-3">
                                                    <p className="text-sm leading-relaxed">
                                                        Brand Genius is the control panel for your brand. Use Wizard Mode to get it to a strong level, and use Form Mode for reviewing and tweaking.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    {/* Desktop-only description text - mobile users see tooltip instead */}
                                    <p className="text-gray-600 text-base md:text-lg leading-relaxed hidden md:block">
                                        Brand Genius is the control panel for your brand. Use Wizard Mode to get it to a strong level, and use Form Mode for reviewing and tweaking.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center h-[calc(100vh-20rem)]">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-indigo-200 rounded-full blur-xl opacity-30 animate-pulse" />
                            <LoadingSpinner size="lg" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
                
                {/* Enhanced Header Section with Gradient Background */}
                <div className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/40 rounded-2xl border border-purple-200/50 shadow-sm p-4 md:p-8">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                    <div className="relative">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 md:gap-6">
                            <div className="flex items-center gap-2 md:gap-4 flex-1">
                                <div className="p-2 md:p-3 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg">
                                    <BrainIcon className="h-5 w-5 md:h-8 md:w-8 text-purple-600" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 md:mb-2">
                                        <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 bg-clip-text text-transparent">Brand Genius</h1>
                                        {/* Tooltip icon - visible on all screen sizes */}
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button 
                                                        type="button"
                                                        className="p-1.5 rounded-full hover:bg-purple-100/50 transition-colors flex-shrink-0"
                                                        aria-label="Brand Genius information"
                                                    >
                                                        <Info className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="max-w-xs p-3">
                                                    <p className="text-sm leading-relaxed">
                                                        Your brand is the center of it all, driving your AI personality and preferences. Ensure it's fully set up for the best results!
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    {/* Desktop-only description text - mobile users see tooltip instead */}
                                    <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-2xl hidden md:block">
                                        Your brand is the center of it all, driving your AI personality and preferences. Ensure it's fully set up for the best results!
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-6">
                            {isAdmin && (
                                <>
                                    <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-xl p-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleExportBrand}
                                            className="bg-white/50 border-white/50 hover:bg-white/70 transition-colors"
                                            title="Export brand data as JSON"
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            Export
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleImportBrand}
                                            className="bg-white/50 border-white/50 hover:bg-white/70 transition-colors"
                                            title="Import brand data from JSON"
                                        >
                                            <Upload className="h-4 w-4 mr-2" />
                                            Import
                                        </Button>
                                    </div>
                                    <div className="w-px h-8 bg-purple-200/50" />
                                </>
                            )}
                            <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-xl p-2">
                                <Button
                                    variant={mode === 'form' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setMode('form')}
                                    className={mode === 'form' 
                                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white border-transparent' 
                                        : 'bg-white/50 border-white/50 hover:bg-white/70 transition-colors'
                                    }
                                >
                                    <LayoutGrid className="h-4 w-4 mr-2" />
                                    Form Mode
                                </Button>
                                <Button
                                    variant={mode === 'wizard' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setMode('wizard')}
                                    className={mode === 'wizard' 
                                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white border-transparent' 
                                        : 'bg-white/50 border-white/50 hover:bg-white/70 transition-colors'
                                    }
                                >
                                    <Workflow className="h-4 w-4 mr-2" />
                                    Wizard Mode
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <Toaster />

                {/* Content Area with Enhanced Background */}
                <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/20 via-transparent to-indigo-50/20" />
                    <div className="relative">
                        {mode === 'form' ? (
                            <BrandGeniusForm
                                localCurrentBrand={localCurrentBrand}
                                setLocalCurrentBrand={setLocalCurrentBrand}
                                updateBrandField={updateBrandField}
                            />
                        ) : (
                            <BrandGeniusWizard
                                localCurrentBrand={localCurrentBrand}
                                setLocalCurrentBrand={setLocalCurrentBrand}
                                updateBrandField={updateBrandField}
                                onSwitchToFormMode={() => setMode('form')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Export the component directly without HOC wrapper
export default BrandGenius
